import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Product, CartItem, OrderItemOption, OrderItemAddon } from '@/types'
import { formatCurrency } from '@/utils/formatters'

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref<CartItem[]>([])
  const deliveryFee = ref(0)
  const discount = ref(0)

  // Getters
  const itemCount = computed(() => 
    items.value.reduce((sum, item) => sum + item.quantity, 0)
  )

  const subtotal = computed(() =>
    items.value.reduce((sum, item) => sum + item.total, 0)
  )

  const total = computed(() => subtotal.value + deliveryFee.value - discount.value)

  const isEmpty = computed(() => items.value.length === 0)

  const formattedSubtotal = computed(() => formatCurrency(subtotal.value))
  const formattedDeliveryFee = computed(() => formatCurrency(deliveryFee.value))
  const formattedDiscount = computed(() => formatCurrency(discount.value))
  const formattedTotal = computed(() => formatCurrency(total.value))

  // Actions
  const addItem = (
    product: Product,
    quantity: number = 1,
    selectedOptions: OrderItemOption[] = [],
    selectedAddons: OrderItemAddon[] = [],
    notes: string = ''
  ) => {
    // Calcula o preço total do item
    let itemPrice = product.current_price
    
    // Adiciona preço das opções
    selectedOptions.forEach(option => {
      itemPrice += option.price
    })
    
    // Adiciona preço dos adicionais
    selectedAddons.forEach(addon => {
      itemPrice += addon.price
    })
    
    const total = itemPrice * quantity

    // Verifica se o item já existe no carrinho
    const existingItemIndex = items.value.findIndex(item => 
      item.product.id === product.id &&
      JSON.stringify(item.selected_options) === JSON.stringify(selectedOptions) &&
      JSON.stringify(item.selected_addons) === JSON.stringify(selectedAddons)
    )

    if (existingItemIndex >= 0) {
      // Atualiza quantidade do item existente
      items.value[existingItemIndex].quantity += quantity
      items.value[existingItemIndex].total += total
    } else {
      // Adiciona novo item
      items.value.push({
        product,
        quantity,
        selected_options: selectedOptions,
        selected_addons: selectedAddons,
        notes,
        total
      })
    }

    saveToStorage()
  }

  const removeItem = (index: number) => {
    items.value.splice(index, 1)
    saveToStorage()
  }

  const updateQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(index)
      return
    }

    const item = items.value[index]
    const unitPrice = item.total / item.quantity
    item.quantity = quantity
    item.total = unitPrice * quantity
    
    saveToStorage()
  }

  const updateItemOptions = (
    index: number,
    options: OrderItemOption[],
    addons: OrderItemAddon[]
  ) => {
    const item = items.value[index]
    item.selected_options = options
    item.selected_addons = addons
    
    // Recalcula o total
    let itemPrice = item.product.current_price
    options.forEach(option => {
      itemPrice += option.price
    })
    addons.forEach(addon => {
      itemPrice += addon.price
    })
    
    item.total = itemPrice * item.quantity
    saveToStorage()
  }

  const setDeliveryFee = (fee: number) => {
    deliveryFee.value = fee
    saveToStorage()
  }

  const setDiscount = (value: number) => {
    discount.value = value
    saveToStorage()
  }

  const clearCart = () => {
    items.value = []
    deliveryFee.value = 0
    discount.value = 0
    saveToStorage()
  }

  const saveToStorage = () => {
    localStorage.setItem('cart', JSON.stringify({
      items: items.value,
      deliveryFee: deliveryFee.value,
      discount: discount.value
    }))
  }

  const loadFromStorage = () => {
    const stored = localStorage.getItem('cart')
    if (stored) {
      try {
        const data = JSON.parse(stored)
        items.value = data.items || []
        deliveryFee.value = data.deliveryFee || 0
        discount.value = data.discount || 0
      } catch {
        localStorage.removeItem('cart')
      }
    }
  }

  const getItemsForOrder = () => {
    return items.value.map(item => ({
      product_id: item.product.id,
      quantity: item.quantity,
      selected_options: item.selected_options || [],
      selected_addons: item.selected_addons || [],
      notes: item.notes || ''
    }))
  }

  return {
    // State
    items,
    deliveryFee,
    discount,
    // Getters
    itemCount,
    subtotal,
    total,
    isEmpty,
    formattedSubtotal,
    formattedDeliveryFee,
    formattedDiscount,
    formattedTotal,
    // Actions
    addItem,
    removeItem,
    updateQuantity,
    updateItemOptions,
    setDeliveryFee,
    setDiscount,
    clearCart,
    loadFromStorage,
    getItemsForOrder
  }
})
